import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@lensora/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'


import { ResendConfirmationButton } from '@/components/auth/ResendConfirmationButton'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/providers/auth-provider'

// ── Types ─────────────────────────────────────────────────────────────────────
interface ValidateResult {
  email: string
  expiresAt: string
}

interface ConfirmResult {
  confirmed: true
  alreadyConfirmed: boolean
  creditsGranted: number
}

// ── Data hooks ────────────────────────────────────────────────────────────────
function useValidateConfirmation(token: string | null) {
  return useQuery({
    queryKey: ['signup-confirm-validate', token],
    queryFn: () => api.post<ValidateResult>('/api/auth/signup/confirm/validate', { token }),
    enabled: !!token,
    retry: false,
  })
}

// Fires only from ReadyState's button onClick — never from a query, effect,
// or mount handler. A corporate mail-scanner or link-preview bot issuing a
// GET-equivalent fetch against the emailed URL can only ever reach the
// validate query above (read-only); it has no way to trigger this.
function useConfirmMutation() {
  return useMutation({
    mutationFn: (token: string) => api.post<ConfirmResult>('/api/auth/signup/confirm', { token }),
  })
}

// ── Sub-components ────────────────────────────────────────────────────────────
function ConfirmEmailShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-raised p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-lg bg-brand-primary" />
          <h1 className="text-2xl font-bold text-text">Confirm your email</h1>
        </div>
        <Card>{children}</Card>
      </div>
    </div>
  )
}

function InvalidState() {
  return (
    <CardContent className="pt-6 text-center space-y-4">
      <CardTitle className="text-base">This confirmation link isn't valid</CardTitle>
      <CardDescription>
        It may have been mistyped or already used from a different device. Request a new one from the sign-in page.
      </CardDescription>
      <Button asChild className="w-full">
        <Link to="/login">Back to sign in</Link>
      </Button>
    </CardContent>
  )
}

function AlreadyConfirmedState({ destination }: { destination: string }) {
  return (
    <CardContent className="pt-6 text-center space-y-4">
      <CardTitle className="text-base">You're already confirmed</CardTitle>
      <CardDescription>This link has already been used — your account is active and ready to go.</CardDescription>
      <Button asChild className="w-full">
        <Link to={destination}>Continue</Link>
      </Button>
    </CardContent>
  )
}

function ExpiredState() {
  return (
    <CardContent className="pt-6 space-y-4">
      <div className="text-center">
        <CardTitle className="text-base">This link has expired</CardTitle>
        <CardDescription className="mt-1.5">Confirmation links expire after a while. Request a new one below.</CardDescription>
      </div>
      <ResendConfirmationButton />
    </CardContent>
  )
}

function ReadyState({ token, email, destination }: { token: string; email: string; destination: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const confirmMutation = useConfirmMutation()
  const [justConfirmed, setJustConfirmed] = React.useState(false)

  function handleConfirm() {
    confirmMutation.mutate(token, {
      onSuccess: () => {
        // alreadyConfirmed: true / creditsGranted: 0 is still a success —
        // there is no separate branch here that treats it as an error.
        void queryClient.invalidateQueries({ queryKey: ['billing-status'] })
        void queryClient.invalidateQueries({ queryKey: ['me'] })
        setJustConfirmed(true)
        setTimeout(() => navigate(destination, { replace: true }), 1200)
      },
    })
  }

  if (justConfirmed) {
    return (
      <CardContent className="pt-6 text-center space-y-2">
        <CardTitle className="text-base">Email confirmed</CardTitle>
        <CardDescription>Redirecting you now…</CardDescription>
      </CardContent>
    )
  }

  const error = confirmMutation.error
  const errorMessage = error instanceof ApiError ? error.message : error ? 'Something went wrong. Please try again.' : null

  return (
    <>
      <CardHeader>
        <CardTitle className="text-base">Confirm {email}</CardTitle>
        <CardDescription>Click below to activate your account and unlock your free trial credits.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {errorMessage && <p className="text-sm text-feedback-error">{errorMessage}</p>}
        <Button className="w-full" disabled={confirmMutation.isPending} onClick={handleConfirm}>
          {confirmMutation.isPending ? 'Confirming…' : 'Confirm my email'}
        </Button>
        {error instanceof ApiError && error.code === 'CONFIRM_EXPIRED' && <ResendConfirmationButton />}
      </CardContent>
    </>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function ConfirmEmailPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const auth = useAuth()
  const destination = auth.status === 'authenticated' ? '/dashboard' : '/login?confirmed=1'

  // Frozen on first render — the token lives in this ref/state from here on,
  // never re-read from the URL.
  const tokenRef = React.useRef<string | null>(searchParams.get('token'))
  const token = tokenRef.current

  // Runs once, on mount, regardless of subsequent searchParams identity —
  // stripping the token from the URL bar is a one-time action.
  React.useEffect(() => {
    if (searchParams.has('token')) setSearchParams({}, { replace: true })
  }, [])

  const validate = useValidateConfirmation(token)

  return (
    <ConfirmEmailShell>
      {!token ? (
        <InvalidState />
      ) : validate.isLoading ? (
        <CardContent className="flex justify-center py-10">
          <Spinner size="lg" />
        </CardContent>
      ) : validate.isError ? (
        (() => {
          const code = validate.error instanceof ApiError ? validate.error.code : undefined
          if (code === 'CONFIRM_USED') return <AlreadyConfirmedState destination={destination} />
          if (code === 'CONFIRM_EXPIRED') return <ExpiredState />
          return <InvalidState />
        })()
      ) : validate.data ? (
        <ReadyState token={token} email={validate.data.email} destination={destination} />
      ) : null}
    </ConfirmEmailShell>
  )
}
